import type { ActivitySettingsModel } from '@/shared/generated'

import { pipe } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { intlFormat } from '@/shared/tools/dates'
import {
	getEnumOptions,
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export const ActivityView = ({
	settings,
}: {
	settings: ActivitySettingsModel
}) => {
	const { t } = useTranslation()
	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:activity'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})

	if (settingsSchema.isLoading) {
		return (
			<Card className='shadow-none'>
				<ActivityViewSkeleton />
			</Card>
		)
	}

	const schema = settingsSchema.data

	return (
		<Card className='shadow-none'>
			<CardHeader>
				<CardTitle>{schema.title}</CardTitle>
			</CardHeader>
			<CardContent className='flex flex-col gap-4 divide-y'>
				<DescriptionList className='gap-4'>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{schema.properties.start_date.title}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{settings.start_date
								? intlFormat(new Date(settings.start_date))
								: '-'}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{schema.properties.end_date.title}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{settings.end_date
								? intlFormat(new Date(settings.end_date))
								: '-'}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{schema.properties.repeat_week_interval.title}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{settings.repeat_week_interval}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{schema.properties.repeat_days.title}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{settings.repeat_days
								.map((day) =>
									t(day, {
										defaultValue: day,
									})
								)
								.join(', ') || '-'}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{schema.properties.inactive_strategy.title}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{
								getEnumOptions(schema.properties.inactive_strategy).find(
									(item) => item.value === settings.inactive_strategy
								)?.label
							}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{schema.properties.force_inactive.title}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{settings.force_inactive
								? t('yes', {
										defaultValue: 'Yes',
									})
								: t('no', {
										defaultValue: 'No',
									})}
						</DescriptionDetail>
					</DescriptionGroup>
				</DescriptionList>
			</CardContent>
		</Card>
	)
}
const ActivityViewSkeleton = () => (
	<div className='w-full space-y-4 p-4'>
		<Skeleton className='h-6 w-42' />
		<div className='w-full space-y-4'>
			<Skeleton className='h-6 w-42' />
			<Skeleton className='h-6 w-24' />
			<Skeleton className='h-6 w-32' />
			<Skeleton className='h-6 w-24' />
			<Skeleton className='h-6 w-32' />
			<Skeleton className='h-6 w-42' />
			<Skeleton className='h-6 w-24' />
			<Skeleton className='h-6 w-12' />
		</div>
	</div>
)
